// shareAnalytics.ts — CORE-788
//
// Typed catalog + emit helpers for «Поделиться». Thin layer over
// `analytics.track(...)` (→ the existing `AnalyticsEvents` store) — not a new
// stream, exactly like `homeAnalytics.ts` (CORE-595) and
// `gamificationAnalytics.ts` (CORE-711).
//
// ── Why this file exists ────────────────────────────────────────────────────
// Before CORE-788 sharing was invisible: not one `share_*` row had ever been
// written, so «сколько раз и чем делятся» had no answer at all. The trap was
// that it *looked* instrumented — `gamificationAnalytics.ts` has a
// `LightPathAction = 'share'` and a `LightPathSource = 'share_link'` — but the
// gamification report aggregates only views and never breaks clicks down by
// action, and nothing ever set `share_link`. An event nobody counts and a
// property nobody sets are indistinguishable from no instrumentation.
//
// ── Why the vocabulary is typed ─────────────────────────────────────────────
// Sharing is spread across two choke points and ~30 call sites. If each site
// spelled its own `surface`, the report would aggregate garbage — and a
// misspelling fails nothing, it just produces a row nobody counts. So every
// helper takes a typed argument object: a wrong surface is a compile error.
//
// The BE mirror of the event names lives in
// `Agape.Shared/Models/AnalyticsEventNames.cs` (`AnalyticsEventNames.Share`).
// Keep both lists in lock-step: renaming on one side empties the report without
// breaking a build.

import { analytics } from './analytics';

/**
 * Canonical event names. Values are the exact strings persisted to
 * `AnalyticsEvents.EventName` — the report keys off them, so they are stable.
 */
export const ShareEvents = {
  /** The user reached for sharing (sheet opened / OS sheet invoked). */
  clicked: 'share_clicked',
  /** How it ended. Separate row because intent and outcome cannot share one. */
  completed: 'share_completed',
  /** Somebody arrived through a shared link. */
  linkOpened: 'share_link_opened',
} as const;

export type ShareEventName = (typeof ShareEvents)[keyof typeof ShareEvents];

/**
 * What is being shared. Mirrors `ShareableType` in `src/utils/share.ts` plus
 * the surfaces that share something without a `buildShareUrl` route of their
 * own (gamification cards, the fund page).
 */
export type ShareEntityKind =
  | 'live'
  | 'room'
  | 'practice'
  | 'event'
  | 'master'
  | 'user'
  | 'master_post'
  | 'gamification'
  | 'fund';

/**
 * Where the share was initiated. One value per screen/component that offers
 * the affordance — this is the axis that answers «откуда вообще делятся».
 */
export type ShareSurface =
  | 'calendar'            // /lives calendar cards (web + native hub)
  | 'daily_rhythm'        // home «Ритм дня» strip
  | 'my_calendar'         // MyCalendarPanel
  | 'practices'           // library / practices list
  | 'practice_detail'     // PracticeDetailScreen
  | 'practice_room_card'  // PracticeRoomCard, PracticeRooms/{Upcoming,Archive}Card
  | 'room_announcement'   // pre-broadcast page («предбанник»)
  | 'live_detail'         // LiveDetailScreen
  | 'live_room'           // inside the room (header button, chat panel)
  | 'room_ending'         // CORE-1762 — the ending page of a finished Live («Поделиться итогами»)
  | 'home'                // home screen
  | 'hero'                // Hero block
  | 'feed'                // dashboard FeedCard / CommunityFeed
  | 'event_card'          // EventCard / MasterEventModal
  | 'chat'                // room invite sent from a chat thread
  | 'profile'             // own/public profile share
  | 'masters'             // /masters catalogue card
  | 'light_path'          // «Путь Света» gamification tab + celebration modals
  | 'agape_practice'      // Agapē Practice screen (native)
  | 'live_management'     // CORE-1526 — «Скопировать ссылку» в «Предстоящих» кабинета
  | 'fund';               // fund page

/**
 * Which channel actually carried it. `native_sheet` / `web_share` / `clipboard`
 * come from `shareEntity()`; the named messengers come from the in-app
 * `ShareModal` tiles, which know exactly where they sent the user.
 */
export type ShareChannel =
  | 'telegram'
  | 'whatsapp'
  | 'facebook'
  | 'email'
  | 'circle'        // repost into the master's circle
  | 'chat'          // posted straight into an Agapella conversation
  | 'clipboard'
  | 'native_sheet'  // OS share sheet (native)
  | 'web_share';    // navigator.share

/** How the attempt ended. `shareEntity()` already returns exactly this union. */
export type ShareOutcome = 'shared' | 'copied' | 'dismissed';

/** The thing being shared, and from where. Carried by every share event. */
export interface ShareTarget {
  entityType: ShareEntityKind;
  /** Numeric id where there is one. Non-numeric ids are dropped — see below. */
  entityId?: string | number | null;
  /**
   * A bounded vocabulary sub-kind, for entities whose identifier is a name
   * rather than a number — the gamification card type (`level`, `achievement`,
   * `streak`…), for instance. Must come from a fixed set defined in code:
   * anything user-supplied belongs nowhere near an analytics property.
   */
  entityKey?: string;
  surface: ShareSurface;
}

// ── Privacy / secret backstop ───────────────────────────────────────────────

/**
 * Property keys that must never reach `AnalyticsEvents`: identity and the
 * free text of whatever is being shared. The report needs to know *that* a
 * practice was shared and from where — never its title, and never who by
 * (the BE stamps the authoritative `UserId` from the JWT already).
 */
const FORBIDDEN_KEYS = [
  'name', 'displayName', 'email', 'phone', 'userName',
  'title', 'text', 'message', 'subject', 'description', 'url', 'link',
];

/** Drop identity / free-text keys. Exported for the test. */
export function scrubShareProps<T extends Record<string, unknown>>(props: T): T {
  const out = { ...props };
  for (const key of Object.keys(out)) {
    if (FORBIDDEN_KEYS.includes(key)) delete out[key];
  }
  return out;
}

/**
 * Ids reach us in two shapes, and only one of them is safe to store.
 *
 * A room is shared by its **invite code** (`shareEntity({ type: 'room', id:
 * room.inviteCode })`), and since CORE-721 that code is a capability: whoever
 * holds it can enter a private Live. Writing it into an analytics table would
 * copy an access token into a store built for counting, read by admins and
 * exported at will. So a non-numeric id is never persisted — we record only
 * that the id *was* a code, which is all the report needs to keep the two
 * kinds of row apart.
 */
export function describeEntityId(id?: string | number | null): {
  entityId?: number;
  entityIdKind: 'numeric' | 'code' | 'none';
} {
  if (id == null || id === '') return { entityIdKind: 'none' };
  const asNumber = typeof id === 'number' ? id : Number(String(id).trim());
  if (Number.isFinite(asNumber) && String(id).trim() !== '') {
    return { entityId: asNumber, entityIdKind: 'numeric' };
  }
  return { entityIdKind: 'code' };
}

function emit(event: ShareEventName, props: Record<string, unknown>): void {
  analytics.track(event, scrubShareProps(props) as Record<string, any>);
}

function targetProps(target: ShareTarget): Record<string, unknown> {
  return {
    entityType: target.entityType,
    surface: target.surface,
    ...describeEntityId(target.entityId),
    ...(target.entityKey ? { entityKey: target.entityKey } : {}),
  };
}

// ── Emit helpers ────────────────────────────────────────────────────────────

/**
 * The user reached for sharing — the in-app sheet opened, or the OS sheet was
 * invoked. Emitted before anything can fail, so a channel that never opens is
 * still counted as intent; `share_completed` is what says whether it landed.
 */
export function trackShareClicked(target: ShareTarget): void {
  emit(ShareEvents.clicked, targetProps(target));
}

/**
 * How the share ended. Always paired with a preceding `share_clicked` from the
 * same surface, so `completed / clicked` reads as a completion rate and a
 * dismissed sheet is visible rather than merely absent.
 */
export function trackShareCompleted(
  target: ShareTarget & { channel?: ShareChannel; outcome: ShareOutcome },
): void {
  emit(ShareEvents.completed, {
    ...targetProps(target),
    // A dismissed in-app sheet has no channel — the user picked nothing. The
    // key is omitted rather than filled with a placeholder, so «closed without
    // choosing» stays distinguishable from a channel that failed.
    ...(target.channel ? { channel: target.channel } : {}),
    outcome: target.outcome,
  });
}

/**
 * Somebody opened a link that came out of one of the shares above (the URL
 * carries `?ref=share`, added centrally by `buildShareUrl`).
 *
 * Without this the counters answer only «жали ли кнопку». Whether sharing
 * brings anyone in is a different question, and it is the one worth asking.
 */
export function trackShareLinkOpened(args: {
  entityType: ShareEntityKind;
  entityId?: string | number | null;
  isAuthenticated: boolean;
}): void {
  emit(ShareEvents.linkOpened, {
    entityType: args.entityType,
    ...describeEntityId(args.entityId),
    isAuthenticated: args.isAuthenticated,
  });
}

// ── Inbound-link parsing ────────────────────────────────────────────────────

/** The query flag `buildShareUrl` stamps onto every generated link. */
export const SHARE_REF_PARAM = 'ref';
export const SHARE_REF_VALUE = 'share';

/**
 * Which entity a shared path points at, derived from the route table in
 * `src/utils/share.ts`. Returns null for anything that is not a shareable
 * route, so an unrelated link carrying `?ref=share` cannot invent a row.
 *
 * Mirrors `pathFor()` — keep the two in step if a route is renamed.
 */
export function entityFromSharePath(
  pathname: string,
  search?: string,
): { entityType: ShareEntityKind; entityId?: string } | null {
  const path = (pathname || '').replace(/\/+$/, '') || '/';

  const post = path.match(/^\/u\/([^/]+)\/post\/([^/]+)$/);
  if (post) return { entityType: 'master_post', entityId: post[2] };

  const user = path.match(/^\/u\/([^/]+)$/);
  if (user) return { entityType: 'user', entityId: user[1] };

  const live = path.match(/^\/live\/([^/]+)$/);
  if (live) return { entityType: 'live', entityId: live[1] };

  const room = path.match(/^\/room\/([^/]+)$/);
  if (room) return { entityType: 'room', entityId: room[1] };

  const practice = path.match(/^\/practices\/([^/]+)$/);
  if (practice) return { entityType: 'practice', entityId: practice[1] };

  // An offline event has no page of its own — it opens as a modal on the
  // calendar, so the shareable form is `/lives?eventId=` (CORE-630).
  if (path === '/lives') {
    const eventId = new URLSearchParams(search || '').get('eventId');
    if (eventId) return { entityType: 'event', entityId: eventId };
  }

  return null;
}
